import { Button } from "@/common/components/button";
import { DRFWrapper } from "./styles";
import Table from "@/common/components/Table";
import { Modal } from "@/common/components/modal";
import { useMemo, useState } from "react";
import Form from "@/common/components/form/Form";
import { ModalWrapper } from "./styles";
import success from "@/assets/images/success_alt.png";
import { getFormInputs, TableKeys } from "./data";
import { TableData, TableHeaders } from "./data";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import {
  DRF_ACTIVATE_USER_URL,
  DRF_DEACTIVATE_USER_URL,
  DRF_ROLES,
  DRF_USERS,
  INVITE_DRF_URL,
} from "@/common/constants";
import { useNotification } from "@/common/components/Notifications/NotificationProvider";
import useQuery from "@/hooks/useQuery";
import { KeyValueObj } from "@/common/types";
import { Pagination } from "@/common/components/pagination";
import { Prompt } from "@/common/components/prompt";
import { EUserStatus } from "@/types/enum";

enum EToggleUserStatus {
  ACTIVATE = "activate",
  DEACTIVATE = "deactivate",
}

enum EModal {
  INVITE = "invite",
  REDIRECT = "redirect",
  ACTIVATE = "activate",
  DEACTIVATE = "deactivate",
}

interface IFieldValue {
  value: string;
  type: string;
}

interface IUserTableData {
  id: string;
  email: IFieldValue;
  firstName: IFieldValue;
  lastName: IFieldValue;
  phone: IFieldValue;
  role: IFieldValue;
  status: IFieldValue;
}

export default function DRF() {
  const [activeModal, setActiveModal] = useState<EModal | null>(null);
  const [currentRow, setCurrentRow] = useState<IUserTableData | null>(null);

  const [submitLoading, setSubmitLoading] = useState(false);

  const [pagination, setPagination] = useState<KeyValueObj>({
    page: 1,
    per_page: 10,
  });

  const makeRequest = useApiCallHandling();
  const notify = useNotification();

  const {
    data: users,
    loading: usersLoading,
    meta: usersMeta,
    refetch: refetchUsers,
  } = useQuery(
    DRF_USERS + `?page=${pagination.page}&per_page=${pagination.per_page}`
  );

  const { data: roles } = useQuery(DRF_ROLES + `?page=${1}&per_page=${20}`);

  const handleDropdownClick = (row: IUserTableData) => {
    if (row.id === currentRow?.id) {
      setCurrentRow(null);
    } else {
      setCurrentRow(row);
    }
  };

  const userDropdowns = useMemo(
    () =>
      currentRow?.status.value === EUserStatus.DEACTIVATED
        ? [
            {
              label: "Activate",
              action: () => {
                setActiveModal(EModal.ACTIVATE);
              },
            },
          ]
        : [
            {
              label: "Deactivate",
              action: () => {
                setActiveModal(EModal.DEACTIVATE);
              },
            },
          ],
    [currentRow]
  );

  const handleSubmit = (formValues: any) => {
    setSubmitLoading(true);

    makeRequest(
      INVITE_DRF_URL,
      "POST",
      formValues,
      (_) => {
        setActiveModal(EModal.REDIRECT);
      },
      (error) => {
        notify({
          message: error.response?.data?.message || "Error inviting user",
          type: "ERROR",
        });
      },
      () => {
        setSubmitLoading(false);
      },
      { dontNotifyOnSucess: true, dontNotifyonFail: true }
    );
  };

  const toggleUserStatus = (user_id: string, toggleTo: EToggleUserStatus) => {
    setSubmitLoading(true);

    toggleTo === EToggleUserStatus.DEACTIVATE
      ? makeRequest(
          DRF_DEACTIVATE_USER_URL,
          "POST",
          { user_id },
          () => {},
          () => {},
          () => {
            setSubmitLoading(false);
            setActiveModal(null);
            setCurrentRow(null);
            refetchUsers();
          }
        )
      : makeRequest(
          DRF_ACTIVATE_USER_URL,
          "POST",
          { user_id },
          () => {},
          () => {},
          () => {
            setSubmitLoading(false);
            setActiveModal(null);
            setCurrentRow(null);
            refetchUsers();
          }
        );
  };

  return (
    <DRFWrapper>
      <div className="invite-button">
        <Button onClick={() => setActiveModal(EModal.INVITE)}>
          Invite User
        </Button>
      </div>

      <div className="teble">
        <Table
          tableTitle="Total Users"
          tableHeaders={TableHeaders}
          tableKeys={TableKeys}
          tableData={TableData(users)}
          tableSubtitle={(usersMeta?.total_count ?? "0") + " users"}
          loading={usersLoading}
          handleDropDownClicked={(row: KeyValueObj) =>
            handleDropdownClick(row as unknown as IUserTableData)
          }
          showDropDown={Boolean(currentRow)}
          dropDownOptions={userDropdowns}
          spaced
          rowLineColor="light"
        />

        <Pagination
          pageCount={usersMeta?.total_pages || 1}
          onNextPage={(event) =>
            setPagination({ ...pagination, page: event.selected })
          }
          onPerPageChange={(event) =>
            setPagination({
              ...pagination,
              per_page: Number(event?.target?.value),
            })
          }
        />
      </div>

      <div>
        <Modal
          title="Invite User"
          isOpen={activeModal === EModal.INVITE}
          close={() => setActiveModal(null)}
        >
          <div className="modal max-w-[600px] md:w-[600px] min-h-[300px] bg-white">
            <Form
              formInputs={getFormInputs(roles)}
              processInputs={handleSubmit}
              loadingState={submitLoading}
            />
          </div>
        </Modal>

        <Modal
          isOpen={activeModal === EModal.REDIRECT}
          close={() => setActiveModal(null)}
        >
          <ModalWrapper>
            <img src={success} alt="success" />
            <p>User Successfully Created</p>

            <Button onClick={() => setActiveModal(null)}>Done</Button>
          </ModalWrapper>
        </Modal>

        <Modal
          isOpen={
            (activeModal === EModal.ACTIVATE ||
              activeModal === EModal.DEACTIVATE) &&
            !!currentRow
          }
          close={() => {
            setCurrentRow(null);
            setActiveModal(null);
          }}
        >
          <Prompt
            prompt={`Are you sure you want to ${
              activeModal === EModal.DEACTIVATE ? "deactivate" : "activate"
            } user`}
            type={activeModal === EModal.DEACTIVATE ? "delete" : "accept"}
            onMainAction={(_?: any) => {
              toggleUserStatus(
                currentRow?.id as string,
                // @ts-ignore
                activeModal
              );
            }}
            onSecondaryAction={() => {
              setCurrentRow(null);
              setActiveModal(null);
            }}
            mainLoading={submitLoading}
          />
        </Modal>
      </div>
    </DRFWrapper>
  );
}
